import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { NavItem } from '../../types/command-palette';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '../../ui/sidebar';

export interface SidebarNavGroupProps {
  label: string;
  items: NavItem[];
  className?: string;
  /**
   * Replace an item's icon with custom content — used for the Profile row, which shows the
   * signed-in user's avatar instead of a generic icon. Return `undefined` to keep the default.
   */
  renderIcon?: (item: NavItem) => ReactNode | undefined;
}

/**
 * Renders one labelled section of sidebar navigation from shared `NavItem` definitions.
 *
 * Both portals' `AppSidebar` had a byte-for-byte copy of this markup plus a sprawling inline
 * `isActive` ternary that had to be edited by hand whenever a route gained children. The
 * highlight rule now travels with the item (`NavItem.isActive`), and the same definitions feed
 * the Ctrl/Cmd+K palette, so sidebar and palette cannot drift apart.
 */
export function SidebarNavGroup({ label, items, className, renderIcon }: SidebarNavGroupProps) {
  const location = useLocation();

  return (
    <SidebarGroup className={className}>
      <SidebarGroupLabel className="group-data-[collapsible=icon]:hidden">
        {label}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            // Items without their own predicate light up only on an exact match — the right
            // default for leaf routes like Calendar that own no children.
            const isActive = item.isActive
              ? location.pathname === item.url || item.isActive(location.pathname)
              : location.pathname === item.url;

            const customIcon = renderIcon?.(item);

            return (
              <SidebarMenuItem key={item.id}>
                <SidebarMenuButton asChild isActive={isActive} tooltip={item.title}>
                  <Link to={item.url}>
                    {customIcon ?? <item.icon className="h-4 w-4" />}
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
